import QRCode from 'qrcode';

// Simple user-facing directions QR: draws a CSAIL TIG branded card around a
// QR code for a directions URL and downloads it as a PNG.
// Separate from the manager QR_POINT signage flow (routes_manager /navigation/qr).

const LOGO_SRC = '/assets/csail-tig.png';
const WIDTH = 600;
const PADDING = 40;
const QR_SIZE = WIDTH - PADDING * 2;
const BORDER_COLOR = '#1F3A68';

function loadImage(src) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null); // the card still renders without the logo
        img.src = src;
    });
}

// Shrinks text until it fits the card width
function fitText(ctx, text, maxWidth, size, weight) {
    let fontSize = size;
    ctx.font = `${weight} ${fontSize}px Roboto, Arial, sans-serif`;
    while (fontSize > 12 && ctx.measureText(text).width > maxWidth) {
        fontSize -= 1;
        ctx.font = `${weight} ${fontSize}px Roboto, Arial, sans-serif`;
    }
}

function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

export default {
    /**
     * @param {String} url   directions URL to encode
     * @param {Object} labels { from: String|null, to: String }
     * @returns {Promise<HTMLCanvasElement>}
     */
    async createCard(url, labels) {
        const qrCanvas = document.createElement('canvas');
        await QRCode.toCanvas(qrCanvas, url, {
            width: QR_SIZE,
            margin: 1,
            errorCorrectionLevel: 'M',
            color: { dark: '#000000', light: '#FFFFFF' }
        });

        const logo = await loadImage(LOGO_SRC);
        const logoH = logo ? 70 : 0;
        const logoW = logo ? logo.width * (logoH / logo.height) : 0;
        const lines = [];
        if (labels.from) lines.push({ label: 'From', text: labels.from });
        lines.push({ label: 'To', text: labels.to });

        const height = PADDING + (logo ? logoH + 24 : 0) + 40 + 16 + QR_SIZE + 24 + lines.length * 36 + 24 + 22 + PADDING;
        const canvas = document.createElement('canvas');
        canvas.width = WIDTH;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        // Card + branded border
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, WIDTH, height);
        ctx.lineWidth = 8;
        ctx.strokeStyle = BORDER_COLOR;
        roundRect(ctx, 4, 4, WIDTH - 8, height - 8, 24);
        ctx.stroke();

        let y = PADDING;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        if (logo) {
            ctx.drawImage(logo, (WIDTH - logoW) / 2, y, logoW, logoH);
            y += logoH + 24;
        }

        ctx.fillStyle = BORDER_COLOR;
        ctx.font = 'bold 34px Roboto, Arial, sans-serif';
        ctx.fillText('Directions', WIDTH / 2, y);
        y += 40 + 16;

        ctx.drawImage(qrCanvas, PADDING, y, QR_SIZE, QR_SIZE);
        y += QR_SIZE + 24;

        ctx.fillStyle = '#000000';
        for (const line of lines) {
            fitText(ctx, `${line.label}: ${line.text}`, WIDTH - PADDING * 2, 26, 'bold');
            ctx.fillText(`${line.label}: ${line.text}`, WIDTH / 2, y);
            y += 36;
        }
        y += 24;

        ctx.fillStyle = '#616161';
        ctx.font = '18px Roboto, Arial, sans-serif';
        ctx.fillText(`Scan with your phone camera · ${window.location.host}`, WIDTH / 2, y);

        return canvas;
    },

    async download(url, labels, fileName) {
        const canvas = await this.createCard(url, labels);
        const link = document.createElement('a');
        link.download = fileName;
        link.href = canvas.toDataURL('image/png');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}
